import Paging from '../../core/Paging';
import { v4 as uuidv4 } from 'uuid';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { makeCancelable, sortObjects } from '../../core/utils/utils';
import GS from '../../grafsync/grafsync';
import CollectionConcept from './CollectionConcept';

const CONCEPT_BATCH = 100;
const PAGE_SIZE = 500;
const MAX_NODES = 5000;

const loadOptions = {
  predicates: [
    "http://www.w3.org/2004/02/skos/core#inScheme",
    "http://www.w3.org/2004/02/skos/core#prefLabel",
    "http://www.w3.org/2002/07/owl#deprecated",
    "http://www.w3.org/2008/05/skos-xl#prefLabel",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type"
  ],
  includeAuth: true,
  tests: [
    {
      id: "http://www.w3.org/2008/05/skos-xl#prefLabel",
      join: [{
        predicate: "http://www.w3.org/2008/05/skos-xl#literalForm"
      }]
    }
  ]
};

class CollectionStructure extends Component {

  constructor(props) {
    super(props);
    this.state = {
      showNumber: CONCEPT_BATCH,
      loadingPage: 0,
      loadingID: undefined,
      page: 1,
      pageManuallyAdjusted: false,
      hasScrolled: false,
      isLoading: true,
      isLoaded: false,
      isErrored: null,
      concepts: [],
      version: null
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.collectData();
  }

  timeMoreConcepts = () => {
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
    if (this.state.showNumber < this.state.concepts.length && this.state.showNumber < PAGE_SIZE) {
      this.moreTimeout = setTimeout(() => {
        this.showMore();
      }, 400);
    }
  };

  static getDerivedStateFromProps(props, state) {
    if ((props.concepts && props.concepts.length > 0 && props.data._version !== state.version)
      || (props.viewLang !== state.viewLang)
      || (props.foundTime !== state.jumpToPageTime)) {
      let page = state.page;
      const concepts = state.concepts;
      if (!state.pageManuallyAdjusted) {
        const fiActive = concepts.findIndex(item => item === props.activeConcept);
        if (fiActive > 0) page = Math.floor(fiActive / PAGE_SIZE) + 1;
      }
      // If a deletion happens that reduces page count and we're on the last page we reset page to 1
      const pageCount = Math.floor((props.data[props.memberProperty].length + PAGE_SIZE - 1) / PAGE_SIZE);
      if (page > pageCount) page = 1;

      return {
        isLoading: true,
        isLoaded: false,
        loadingID: uuidv4(),
        page,
        viewLang: props.viewLang,
        jumpToPageTime: props.foundTime,
        ...(props.foundTime !== state.jumpToPageTime) && { pageManuallyAdjusted: false },
        ...(props.viewLang !== state.viewLang) && { concepts: [] },
        version: props.data._version
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isLoaded && this.state.loadingID !== undefined) this.collectData();
    this.timeMoreConcepts();
  }

  async collectData() {
    // We load ALL the concepts necessary at this level in the tree in one API call to avoid unnecessary calls
    if (this.props.data[this.props.memberProperty] && this.props.data[this.props.memberProperty].length > 0) {
      if (!this.state.isLoaded && !this.state.isErrored) {
        // There may be more than 5000 children so we need to load in pages just in case!
        // We need to load everything because at this point we don't know which belong to this taxonomy
        const loadingID = this.state.loadingID;
        try {
          const pages = Math.floor((this.props.data[this.props.memberProperty].length + MAX_NODES - 1) / MAX_NODES);
          for (let page = 0; page < pages; page++) {
            if (this.state.loadingID !== loadingID) {
              return;
            }
            const pageStart = page * MAX_NODES;
            const pageEnd = (page + 1) * MAX_NODES;
            const pageChildren = this.props.data[this.props.memberProperty].slice(pageStart, pageEnd);
            await this.loadData(pageChildren).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
          }
          // Once all loaded then we sort
          this.sortConcepts();
        }
        catch (e) {
          if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
        }
      }
      else {
        this.setState({ isLoading: false, isLoaded: true });
      }
    }
    else {
      if ((this.cancellable && !this.cancellable.isCancelled()) || !this.cancellable)
        this.setState({ isLoading: false, isLoaded: true });
    }
  }

  sortConcepts(impactsLoading = true) {
    const concepts = this.props.concepts.map(id => GS.data(id, this.props.graph));
    const firstLang = this.props.viewLang || this.props.lang;
    const secondLang = this.props.lang;
    let sortedConcepts;
    if (!this.props.ordered) {
      sortedConcepts = sortObjects(concepts, 'prefLabel', firstLang, secondLang, false)
        .map(concept => concept.id);
    }
    else {
      sortedConcepts = this.props.concepts;
    }
    const isSortEqual = this.state.concepts && sortedConcepts.length === this.state.concepts.length
      && sortedConcepts.every((s, index) => this.state.concepts[index] === s);
    if (isSortEqual && !impactsLoading) {
      // Basically the sort doesn't change anything so don't cause a reload
      return;
    }
    const fiActive = sortedConcepts.findIndex(item => item === this.props.activeConcept);
    const page = !this.state.pageManuallyAdjusted && fiActive >= 0 ?
      Math.floor(fiActive / PAGE_SIZE) + 1
      :
      this.state.page;
    this.setState({
      page,
      isLoading: false,
      isLoaded: true,
      concepts: sortedConcepts,
      showNumber: page === this.state.page ? this.state.showNumber : CONCEPT_BATCH
    });
  }

  loadData(concepts) {
    const { viewLang, defaultLang, graph } = this.props;
    const mergedLoadOptions = Object.assign({}, loadOptions, { language: viewLang && viewLang !== defaultLang ? [defaultLang, viewLang] : defaultLang });
    this.cancellable = makeCancelable(GS.loadData(concepts, mergedLoadOptions, graph, this.props.skosXL));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
  }

  moveConcept = (id, idMovedTo) => {
    let sourceIndex;
    let targetIndex;
    this.props.concepts.forEach((concept, i) => {
      if (concept === id) sourceIndex = i;
      if (concept === idMovedTo) targetIndex = i;
    });
    GS.move(this.props.id, this.props.graph, 'memberList', sourceIndex, targetIndex);
  };

  showMore = () => {
    const showNumber = this.state.showNumber + CONCEPT_BATCH;
    this.setState({ showNumber });
  };

  handlePageChange = async (page = 1) => this.setState({ page, showNumber: CONCEPT_BATCH, pageManuallyAdjusted: true });

  monitorConceptUpdate = (iri, property) => {
    if (property === "prefLabel") {
      if (iri === this.props.activeConcept) {
        this.props.onFindConcept(iri);
      }
      else this.sortConcepts(false);
    }
  };

  render() {

    const { isLoading, isErrored, concepts, showNumber, page } = this.state;
    const { onSelect, lang, onBreakLink, isReadOnly, viewLang, graph, activeConcept, foundTime,
      ordered, defaultLang, foundRef } = this.props;

    if (isErrored) return <Errored data={isErrored} />;

    if (isLoading && concepts.length === 0) return <Loading offset="100px" message={this.props.concepts && this.props.concepts.length > 1000 ?
      <span>This may take a few seconds.<br />There are {this.props.concepts.length} concepts to check.<br />They only get checked once.</span>
      :
      null} />;

    const pageCount = Math.floor((concepts.length + PAGE_SIZE - 1) / PAGE_SIZE);
    const startNode = (page - 1) * PAGE_SIZE;

    const loadOptions = Object.assign({}, { language: viewLang && viewLang !== defaultLang ? [defaultLang, viewLang] : defaultLang });

    return (
      <React.Fragment>
        {pageCount > 1 &&
          <div>
            <Paging
              pageItemsRange={3}
              showText={false}
              page={page}
              pageCount={pageCount}
              handlePageChange={this.handlePageChange} />
          </div>
        }
        {concepts.slice(startNode, startNode + showNumber).map((concept, i) => {
          return <CollectionConcept
            key={concept}
            id={concept}
            graph={graph}
            lang={lang}
            foundRef={foundRef}
            foundTime={foundTime}
            ordered={ordered}
            activeConcept={activeConcept}
            viewLang={viewLang}
            isReadOnly={isReadOnly}
            position={i}
            onSelect={onSelect}
            updateEvent={this.monitorConceptUpdate}
            loadOptions={loadOptions}
            onBreakLink={() => onBreakLink(startNode + i, concept)}
            onMove={this.moveConcept} />;
        })}
        {pageCount > 1 &&
          <div>
            <Paging
              pageItemsRange={3}
              showText={false}
              page={page}
              pageCount={pageCount}
              handlePageChange={this.handlePageChange} />
          </div>
        }
      </React.Fragment>
    );
  }

}

CollectionStructure.propTypes = {
  onBreakLink: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  defaultLang: PropTypes.string.isRequired,
  memberProperty: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  foundTime: PropTypes.number,
  ordered: PropTypes.bool.isRequired,
  onFindConcept: PropTypes.func.isRequired,
  activeConcept: PropTypes.string,
  foundRef: PropTypes.object,
  lang: PropTypes.string.isRequired,
  skosXL: PropTypes.bool,
  graph: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  concepts: PropTypes.array,
  version: PropTypes.number
};

export default CollectionStructure;